import { defineStore } from 'pinia';
import { getCache, setCache } from '@/utils/cache';
import { postSme, getSme, patchSme, putSme } from '@/utils/request';
import axios from 'axios';
import type { SmeVerificationResult } from '@/types';
import { useAuthStore } from '../auth';
import { useSmeCampaignStore } from './campaigns';
import usePlatformAnalytics from '@/composables/usePlatformAnalytics';
import { useSmeMerchantStore } from './sme-merchant';

export const useSmeAuthStore = defineStore('smeAuth', {
  state: () => ({
    error: null as any,
    success: null as any,
    smeToken: getCache('smeToken') || null,
    isSmeLoggedIn: getCache('isSmeLoggedIn') || false,
    smeProfile: getCache('smeProfile') || null,
  }),

  getters: {
    isSmeAuthenticated: state => state.isSmeLoggedIn,
    isSmeAnonymous: state => state.smeProfile === null || state.smeProfile === undefined,
  },

  actions: {
    setError(value: any) {
      this.error = value;
    },

    setSmeToken(token: string) {
      this.smeToken = token;
      this.isSmeLoggedIn = true;

      setCache('smeToken', token);
      setCache('isSmeLoggedIn', true);
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    },

    clearSmeToken() {
      this.smeProfile = null;
      this.smeToken = null;
      this.isSmeLoggedIn = false;

      setCache('smeProfile', null);
      setCache('smeToken', null);
      setCache('isSmeLoggedIn', false);

      // Clear authorization header
      delete axios.defaults.headers.common.Authorization;
    },

    async doSmeLogin({ email, password, router }: any) {
      this.clearSmeToken();
      this.error = null;

      const enterpriseAuth = useAuthStore();

      try {
        const { access_token, merchant_profile, is_sme } = await postSme('/auth/login/merchant', {
          username: email,
          password,
        });

        if (!is_sme) {
          enterpriseAuth.setToken(access_token);
          await enterpriseAuth.doGetProfile();
          if (router) {
            router.push('/companies');
          }
        }

        if (is_sme && access_token) {
          this.setSmeToken(access_token);
          await this.doGetSmeProfile();
        }

        // if (merchant_profile.email_verified) {
        //   this.smeProfile = merchant_profile;
        //   setCache('smeProfile', merchant_profile);
        // }
        return merchant_profile;
      } catch (e) {
        this.error = e;
        throw e;
      }
    },

    async doSmeSignUp(form: any) {
      this.error = null;

      try {
        // Update this endpoint to match your backend
        const { access_token } = await postSme('/merchant/auth/initiate?is_sme=true', form);
        if (access_token) {
          this.setSmeToken(access_token);
        }
      } catch (e: any) {
        this.error = e.message || e;
        throw e;
      }
    },

    async doSmeCodeVerification(code: string): Promise<SmeVerificationResult> {
      this.error = null;

      try {
        // Update this endpoint to match your backend
        const data = await postSme('/auth/merchant/email-verification', {
          otp: code,
        });
        this.smeProfile = data;
        setCache('smeProfile', data);
        return data;
      } catch (e: any) {
        this.error = e.message || e;
        throw e;
      }
    },

    async doSmeResendVerificationCode() {
      this.error = null;

      try {
        // Update this endpoint to match your backend
        const data = await getSme('/auth/merchant/resend-email-verification');
        return data;
      } catch (e: any) {
        this.error = e.message || e;
        throw e;
      }
    },

    async doSmeSendResetEmail(email: string) {
      this.error = null;

      try {
        // Update this endpoint to match your backend
        const data = await postSme('/auth/reset_password/merchant', { email });
        return data;
      } catch (e: any) {
        this.error = e.message || e;
        throw e;
      }
    },

    async doSmeSaveNewPassword(form: { email: string; password: string; token: string }) {
      this.error = null;

      try {
        // Update this endpoint to match your backend
        const data = await postSme('/auth/save_password/merchant', form);
        return data;
      } catch (e: any) {
        this.error = e.message || e;
        throw e;
      }
    },

    async doGetSmeProfile() {
      try {
        const profile = await getSme('/user/merchant');
        this.smeProfile = profile;
        setCache('smeProfile', profile);
        return profile;
      } catch (e) {
        this.error = e;
        throw e;
      }
    },

    async doSmeCompleteSignup(brandName: string) {
      try {
        const merchant_profile = await postSme('/auth/merchant/complete-signup?is_sme=true', {
          business_name: brandName,
        });
        this.smeProfile = merchant_profile;
        setCache('smeProfile', merchant_profile);
        return merchant_profile;
      } catch (e) {
        this.error = e;
        throw e;
      }
    },

    async doUpdateProfile(form: any) {
      const { trackEvent } = usePlatformAnalytics(true);
      const platformEventData: any = {
        event_name: 'SMEProfileUpdated',
        platform: 'sme',
        metadata: {
          user_id: this.smeProfile.id,
        },
      };
      trackEvent(platformEventData);
      this.success = null;
      this.error = null;
      try {
        if (form.phone && form.phone !== this.smeProfile.phone) {
          await patchSme('/user/merchant/phone_number', {
            phone_number: form.phone,
          });
        }

        if (form.file) {
          const formData = new FormData();
          formData.append('file', form.file);

          await postSme('/user/merchant/avatar/upload', formData, {
            'Content-Type': 'multipart/form-data',
          });
        }

        const res = await putSme('/user/merchant', {
          ...form,
          accepted_terms_version: 'V1_MERCHANT_US_EN',
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
        });

        this.smeProfile = res;
        setCache('smeProfile', res);
        return this.smeProfile;
      } catch (e) {
        this.error = e;
      }
    },

    async doSmeLogout() {
      const smeCampaignStore = useSmeCampaignStore();
      this.clearSmeToken();
      setCache('sme-merchant', null);
      smeCampaignStore.$reset();
    },
  },
});
